/**
 * Glass Card - Glassmorphism Container
 *
 * Semi-transparent glass card with backdrop blur effect.
 * Used for all info modules in Mission Control.
 */

import React, { ReactNode } from 'react';
import { Box, Typography, Stack, SxProps, Theme } from '@mui/material';
import { useMissionTheme } from '../theme';

interface GlassCardProps {
  title?: string;
  icon?: ReactNode;
  iconColor?: string;
  children: ReactNode;
  variant?: 'default' | 'highlight' | 'success' | 'warning' | 'error';
  sx?: SxProps<Theme>;
  headerAction?: ReactNode;
}

export default function GlassCard({
  title,
  icon,
  iconColor,
  children,
  variant = 'default',
  sx,
  headerAction,
}: GlassCardProps) {
  const { mc } = useMissionTheme();

  const variantStyles = {
    default: {
      borderColor: mc.glassBorder,
      glowColor: 'transparent',
    },
    highlight: {
      borderColor: mc.secondary,
      glowColor: mc.secondaryGlow,
    },
    success: {
      borderColor: mc.success,
      glowColor: mc.successGlow,
    },
    warning: {
      borderColor: mc.warning,
      glowColor: mc.warningGlow,
    },
    error: {
      borderColor: mc.error,
      glowColor: mc.accentGlow,
    },
  };

  const resolvedIconColor = iconColor ?? mc.secondary;
  const styles = variantStyles[variant];

  return (
    <Box
      sx={{
        p: 2.5,
        background: mc.glass,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderRadius: 3,
        border: `1px solid ${styles.borderColor}`,
        boxShadow: variant !== 'default' ? `0 0 20px ${styles.glowColor}` : 'none',
        transition: 'all 0.3s ease',
        '&:hover': {
          borderColor: variant === 'default' ? mc.secondary : styles.borderColor,
          boxShadow: `0 0 30px ${variant === 'default' ? mc.secondaryGlow : styles.glowColor}`,
        },
        ...sx,
      }}
    >
      {/* Header */}
      {(title || icon) && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            {icon && (
              <Box sx={{ color: resolvedIconColor, display: 'flex', alignItems: 'center' }}>
                {icon}
              </Box>
            )}
            {title && (
              <Typography
                variant="subtitle2"
                sx={{
                  color: mc.textSecondary,
                  fontWeight: 600,
                  letterSpacing: 0.5,
                  textTransform: 'uppercase',
                  fontSize: '0.75rem',
                }}
              >
                {title}
              </Typography>
            )}
          </Stack>
          {headerAction}
        </Stack>
      )}

      {/* Content */}
      {children}
    </Box>
  );
}

/**
 * Glass Info Row - Key-value display row
 */
interface GlassInfoRowProps {
  label: string;
  value: ReactNode;
  highlight?: boolean;
}

export function GlassInfoRow({ label, value, highlight }: GlassInfoRowProps) {
  const { mc } = useMissionTheme();
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.75 }}>
      <Typography
        variant="body2"
        sx={{
          color: mc.textMuted,
          fontSize: '0.85rem',
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: highlight ? mc.accent : mc.textPrimary,
          fontWeight: highlight ? 700 : 500,
          fontSize: '0.85rem',
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

/**
 * Glass Divider - Subtle separator
 */
export function GlassDivider() {
  const { mc } = useMissionTheme();
  return (
    <Box
      sx={{
        height: 1,
        bgcolor: mc.border,
        my: 1.5,
      }}
    />
  );
}
